<template>
    <div>
      <AdminHeader />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Edit User</h5>
          <p class="page-sub">Update employee details and assignments</p>
        </div>
        <button type="button" @click="$router.go(-1)" class="btn-outline">
          <font-awesome-icon icon="arrow-left" /> Back
        </button>
      </div>
  
      <!-- Validation Errors -->
      <div v-if="validationErrors?.length" class="error-banner">
        <font-awesome-icon icon="exclamation-circle" class="error-icon" />
        <ul class="mb-0 ps-1">
          <li v-for="(err, i) in validationErrors" :key="i">{{ err }}</li>
        </ul>
      </div>
  
      <!-- Skeleton while loading -->
      <div v-if="pageLoading" class="form-card">
        <div class="skeleton-wrap">
          <div class="skeleton skeleton--title"></div>
          <div class="skeleton-grid">
            <div class="skeleton skeleton--field" v-for="n in 6" :key="n"></div>
          </div>
        </div>
      </div>
  
      <!-- Form Card -->
      <div v-else class="form-card">
        <form @submit.prevent="editUser">
  
          <!-- User identity badge -->
          <div class="user-badge">
            <div class="user-badge-avatar">{{ first_name?.charAt(0) }}{{ last_name?.charAt(0) }}</div>
            <div>
              <p class="user-badge-name">{{ first_name }} {{ last_name }}</p>
              <p class="user-badge-email">{{ email }}</p>
            </div>
          </div>
  
          <div class="section-divider"></div>
  
          <!-- Personal Info -->
          <div class="section-header">
            <div class="section-icon section-icon--blue">
              <font-awesome-icon icon="user" />
            </div>
            <div>
              <p class="section-title">Personal Information</p>
              <p class="section-sub">Update the user's basic details</p>
            </div>
          </div>
  
          <div class="fields-grid">
            <div class="field-group">
              <label class="field-label">First Name <span class="req">*</span></label>
              <input v-model="first_name" type="text" class="field-input" placeholder="Enter first name" />
            </div>
            <div class="field-group">
              <label class="field-label">Last Name <span class="req">*</span></label>
              <input v-model="last_name" type="text" class="field-input" placeholder="Enter last name" />
            </div>
            <div class="field-group">
              <label class="field-label">Phone Number <span class="req">*</span></label>
              <input v-model="phone_no" type="text" class="field-input" placeholder="e.g. 08012345678" />
            </div>
            <div class="field-group">
              <label class="field-label">Email Address</label>
              <div class="field-disabled-wrap">
                <font-awesome-icon icon="lock" class="field-lock-icon" />
                <input v-model="email" type="email" class="field-input field-input--disabled" disabled />
              </div>
              <span class="field-hint">Email cannot be changed</span>
            </div>
          </div>
  
          <div class="section-divider"></div>
  
          <!-- Role & Assignment -->
          <div class="section-header">
            <div class="section-icon section-icon--purple">
              <font-awesome-icon icon="user-shield" />
            </div>
            <div>
              <p class="section-title">Role & Assignment</p>
              <p class="section-sub">Update role and business/store access</p>
            </div>
          </div>
  
          <div class="fields-grid">
            <div class="field-group">
              <label class="field-label">Role <span class="req">*</span></label>
              <select class="field-input field-select" v-model="role_id" @click="getSelectedRole" id="selectedRole">
                <option value="" disabled>Choose a role</option>
                <option v-for="r in assignableRoles" :key="r.id" :value="r.id">{{ r.name }}</option>
              </select>
            </div>
          </div>
  
          <div class="fields-grid fields-grid--1 mt-3">
            <div class="field-group">
              <label class="field-label">Assigned Business <span class="req">*</span></label>
              <Multiselect
                v-model="business"
                label="name"
                :options="business_arr"
                placeholder="Select business(es)"
                mode="tags"
                :object="true"
                :searchable="true"
                :close-on-select="false"
                @change="getStores"
                class="custom-multiselect"
              />
            </div>
          </div>
  
          <div class="fields-grid fields-grid--1 mt-3">
            <div class="field-group">
              <label class="field-label">Assigned Store <span class="req">*</span></label>
              <Multiselect
                v-model="store"
                label="name"
                :options="store_arr"
                placeholder="Select store(s)"
                mode="tags"
                :object="true"
                :searchable="true"
                :close-on-select="false"
                class="custom-multiselect"
              />
            </div>
          </div>
  
          <!-- Footer -->
          <div class="form-footer">
            <button type="button" @click="$router.go(-1)" class="btn-outline">Cancel</button>
            <button type="submit" class="btn-primary-action" :disabled="crudLoading">
              <span v-if="crudLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
              <font-awesome-icon v-else icon="check" class="me-2" />
              {{ crudLoading ? 'Saving...' : 'Save Changes' }}
            </button>
          </div>
  
        </form>
      </div>
  
    </div>
  </template>
  
  <style src="@vueform/multiselect/themes/default.css"></style>
  
  <script>
  import AdminHeader from './administrationheadertab.vue';
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { useRouter, useRoute } from 'vue-router';
  import { useStore } from 'vuex';
  import Multiselect from '@vueform/multiselect';
  
  export default {
    components: { AdminHeader, Multiselect },
  
    data() {
      return {
        pageLoading: true,
        is_influencer: false,
        first_name: '', last_name: '', phone_no: '',
        email: '', password: '',
        store: [], store_arr: [], stores: {},
        business: [], business_arr: [], businesses: {},
        crudLoading: false, role_id: '', roles: [],
        toast: useToast(), validationErrors: [],
        router: useRouter(), route: useRoute(),
        vueStore: useStore(),
        permission_id: '', company: null, id: null,
        permission_status: null,
      };
    },

    computed: {
      isSuperAdmin() {
        return this.vueStore.state.getLoggedInUser?.is_super == 1;
      },
      assignableRoles() {
        return (this.roles || []).filter(r => {
          // Keep business_owner in the list only when the employee already holds it
          // (super admin editing another super admin). All other users never see it.
          if (r.slug === 'business_owner') return r.id === this.role_id;
          return true;
        });
      },
    },

    async mounted() {
      await this.getRoles();   // roles must exist before role_id is set
      this.singleUser();
      this.getBusinesses();
      this.getStores();
      this.getUserCompany();
    },
  
    methods: {
      getUserCompany() {
        axios.get('/settings/company').then(r => { this.company = r.data.data.data; });
      },
  
      async getRoles() {
        try {
          const r = await axios.get('/admin/role-active');
          this.roles = r.data.data ?? [];
        } catch {
          // If roles endpoint fails (e.g. permission denied), singleUser()
          // will add the employee's current role as a fallback.
        }
      },
  
      getStores() {
        axios.get('/admin/store').then(r => {
          this.stores    = r.data.data ?? [];
          this.store_arr = this.stores.map(s => ({ value: s.id, name: s.store_name }));
        });
      },

      getBusinesses() {
        axios.get('/admin/business').then(r => {
          this.businesses   = r.data.data?.businesses ?? [];
          this.business_arr = this.businesses.map(b => ({ value: b.id, name: b.name }));
        });
      },
  
      getSelectedRole() {},
  
      singleUser() {
        this.pageLoading = true;
        const id = this.route.params.id;
        axios.get('/admin/employee/' + id).then(r => {
          const d = r.data.data;

          // Block non-super-admins from editing a Super Admin user.
          if (d.is_super == 1 && !this.isSuperAdmin) {
            this.toast.error('Only a Super Admin can edit another Super Admin user.');
            this.router.go(-1);
            return;
          }

          this.id            = d.id;
          this.first_name    = d.first_name;
          this.last_name     = d.last_name;
          // Profile records are not always created for employees; fall back to the
          // phone_number field stored directly on the user.
          this.phone_no      = d.profile?.phone_no ?? d.phone_number ?? '';
          this.email         = d.email;
          this.password      = d.password;
          this.permission_id = d.roles?.[0]?.permission?.id ?? '';
          this.role_id       = d.roles?.[0]?.id ?? '';

          // If the employee's current role wasn't returned by getRoles()
          // (e.g. scope mismatch or permission denied), inject it so the
          // dropdown always shows what is actually assigned.
          const currentRole = d.roles?.[0];
          if (currentRole && !this.roles.some(r => r.id === currentRole.id)) {
            this.roles = [currentRole, ...this.roles];
          }

          this.is_influencer = d.roles?.[0]?.name === 'Influencer';
          this.store         = (d.stores ?? []).map(s => ({ value: s.id, name: s.store_name }));
          this.business      = (d.businesses ?? []).map(b => ({ value: b.id, name: b.name }));
          this.pageLoading   = false;
        }).catch(err => {
          this.pageLoading = false;
          this.toast.error(err?.response?.data?.message ?? 'Failed to load user details.');
        });
      },
  
      editUser() {
        this.crudLoading = true;
        const id = this.route.params.id;
        axios.put('/admin/employee/' + id, {
          first_name:    this.first_name,
          last_name:     this.last_name,
          phone_no:      this.phone_no,
          email:         this.email,
          password:      this.password,
          store:         this.store,
          role_id:       this.role_id,
          permission_id: this.permission_id,
        }).then(() => {
          this.crudLoading = false;
          this.router.push('/manage-user');
          this.toast.success('User updated successfully');
          this.validationErrors = '';
        }).catch(error => {
          this.crudLoading = false;
          if (error?.response?.status == 422) {
            this.toast.warning('Your inputs are not valid, kindly recheck');
            this.validationErrors = error.response.data.message;
          }
          if (error?.response?.status == 403) {
            this.permission_status = 403;
            this.toast.error(error.response.data.message);
          }
          if (error?.response?.status == 500) {
            this.toast.error('An error occurred, please try again');
          }
        });
      },
    },
  };
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header {
    display: flex; align-items: flex-start;
    justify-content: space-between; flex-wrap: wrap;
    gap: 12px; margin-bottom: 20px;
  }
  .page-title { font-size: 20px; font-weight: 700; color: #1a2b45; margin: 0 0 3px; }
  .page-sub   { font-size: 13px; color: #7a90b0; margin: 0; }
  
  /* ── Error Banner ── */
  .error-banner {
    display: flex; align-items: flex-start; gap: 10px;
    background: #fff5f5; border: 1px solid #fac5c5;
    border-radius: 10px; padding: 12px 16px;
    margin-bottom: 20px; color: #e03535; font-size: 13px;
  }
  .error-icon { font-size: 16px; margin-top: 2px; flex-shrink: 0; }
  
  /* ── Skeleton ── */
  .skeleton-wrap { padding: 8px 0; }
  .skeleton {
    background: linear-gradient(90deg, #f0f4f8 25%, #e4eaf2 50%, #f0f4f8 75%);
    background-size: 200% 100%;
    animation: shimmer 1.4s infinite;
    border-radius: 8px;
  }
  @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
  .skeleton--title  { height: 20px; width: 160px; margin-bottom: 20px; }
  .skeleton-grid    { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
  .skeleton--field  { height: 42px; }
  
  /* ── Form Card ── */
  .form-card {
    background: #fff; border: 1px solid #e4eaf2;
    border-radius: 12px; padding: 28px;
  }
  
  /* ── User Badge ── */
  .user-badge {
    display: flex; align-items: center; gap: 14px;
    background: #f8fafc; border: 1px solid #e4eaf2;
    border-radius: 10px; padding: 14px 18px;
  }
  .user-badge-avatar {
    width: 46px; height: 46px; border-radius: 50%;
    background: #004AAD; color: #fff;
    display: flex; align-items: center; justify-content: center;
    font-size: 15px; font-weight: 700; text-transform: uppercase; flex-shrink: 0;
  }
  .user-badge-name  { font-size: 14px; font-weight: 700; color: #1a2b45; margin: 0 0 2px; text-transform: capitalize; }
  .user-badge-email { font-size: 12.5px; color: #7a90b0; margin: 0; }
  
  /* ── Divider ── */
  .section-divider { height: 1px; background: #f0f4f8; margin: 22px 0; }
  
  /* ── Section Header ── */
  .section-header { display: flex; align-items: center; gap: 14px; margin-bottom: 18px; }
  .section-icon {
    width: 42px; height: 42px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    font-size: 17px; flex-shrink: 0;
  }
  .section-icon--blue   { background: rgba(0,74,173,.1);   color: #004AAD; }
  .section-icon--purple { background: rgba(124,58,237,.1); color: #7c3aed; }
  .section-title { font-size: 14px; font-weight: 700; color: #1a2b45; margin: 0 0 2px; }
  .section-sub   { font-size: 12px; color: #7a90b0; margin: 0; }
  
  /* ── Fields ── */
  .fields-grid    { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
  .fields-grid--1 { grid-template-columns: 1fr; }
  .field-group    { display: flex; flex-direction: column; gap: 6px; }
  .field-label    { font-size: 13px; font-weight: 600; color: #1a2b45; }
  .req            { color: #e03535; }
  .field-hint     { font-size: 11.5px; color: #aab8cc; }
  
  .field-input {
    padding: 9px 12px; border: 1px solid #dde5f0;
    border-radius: 8px; font-size: 13.5px; color: #1a2b45;
    background: #fff; outline: none; width: 100%;
    transition: border-color .15s, box-shadow .15s;
  }
  .field-input:focus { border-color: #004AAD; box-shadow: 0 0 0 3px rgba(0,74,173,.08); }
  .field-input::placeholder { color: #aab8cc; }
  .field-input--disabled { background: #f8fafc; color: #7a90b0; cursor: not-allowed; padding-left: 34px; }
  .field-select { appearance: auto; cursor: pointer; }
  
  .field-disabled-wrap { position: relative; }
  .field-lock-icon {
    position: absolute; left: 11px; top: 50%;
    transform: translateY(-50%);
    color: #aab8cc; font-size: 12px; pointer-events: none;
  }
  
  /* Multiselect */
  :deep(.custom-multiselect) {
    --ms-border-color: #dde5f0; --ms-border-radius: 8px;
    --ms-font-size: 13.5px; --ms-tag-bg: #edf4ff;
    --ms-tag-color: #004AAD; --ms-ring-color: rgba(0,74,173,.08);
  }
  :deep(.custom-multiselect.is-active) { --ms-border-color: #004AAD; }
  
  /* ── Footer ── */
  .form-footer {
    display: flex; align-items: center;
    justify-content: flex-end; gap: 10px;
    margin-top: 28px; padding-top: 20px;
    border-top: 1px solid #f0f4f8;
  }
  
  .btn-primary-action {
    display: inline-flex; align-items: center;
    background: #004AAD; color: #fff;
    font-size: 13px; font-weight: 600;
    padding: 9px 24px; border-radius: 8px; border: none;
    cursor: pointer; transition: background .15s;
  }
  .btn-primary-action:hover:not(:disabled) { background: #003b8e; }
  .btn-primary-action:disabled { opacity: .6; cursor: not-allowed; }
  
  .btn-outline {
    display: inline-flex; align-items: center; gap: 7px;
    background: #fff; color: #1a2b45;
    border: 1px solid #dde5f0; font-size: 13px; font-weight: 500;
    padding: 9px 20px; border-radius: 8px;
    cursor: pointer; transition: background .15s;
  }
  .btn-outline:hover { background: #f4f7fb; }
  
  /* ── Responsive ── */
  @media (max-width: 768px) {
    .fields-grid  { grid-template-columns: 1fr; }
    .form-card    { padding: 18px; }
    .form-footer  { flex-direction: column; }
    .btn-primary-action,
    .btn-outline  { width: 100%; justify-content: center; }
  }
  </style>